// Push Notification Banner Component
import React, { useState, useEffect } from 'react';
import { Bell, BellOff, X, Check } from 'lucide-react';
import { Button } from './ui/button';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { toast } from 'sonner';

export const NotificationBanner = () => {
  const { 
    isSupported, 
    permission, 
    isSubscribed, 
    isLoading,
    requestPermission,
    unsubscribe 
  } = usePushNotifications();
  
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Show banner after 5 seconds if notifications are supported but not enabled
    const timer = setTimeout(() => {
      if (isSupported && permission === 'default' && !dismissed) {
        const hasSeenBanner = localStorage.getItem('drbooking_notification_banner_seen');
        if (!hasSeenBanner) {
          setShowBanner(true);
        }
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [isSupported, permission, dismissed]);

  const handleEnable = async () => {
    const result = await requestPermission();
    if (result.success) {
      toast.success('Notifications activées ! Vous recevrez des alertes pour vos rendez-vous.');
      setShowBanner(false);
    } else {
      toast.error('Impossible d\'activer les notifications');
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem('drbooking_notification_banner_seen', 'true');
  };

  if (!showBanner || !isSupported) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-slide-up">
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl shadow-2xl p-4 border border-blue-400/30">
        <div className="flex items-start gap-3">
          <div className="bg-white/20 rounded-full p-2 shrink-0">
            <Bell className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-semibold text-sm mb-1">
              Activez les notifications
            </h4>
            <p className="text-blue-100 text-xs mb-3">
              Recevez des rappels pour vos rendez-vous et des alertes quand un créneau se libère.
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleEnable}
                disabled={isLoading}
                className="bg-white text-blue-600 hover:bg-blue-50 text-xs h-8"
                data-testid="enable-notifications-btn"
              >
                <Check className="w-3 h-3 mr-1" />
                {isLoading ? 'Activation...' : 'Activer'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                className="text-white hover:bg-white/10 text-xs h-8"
              >
                Plus tard
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-white/70 hover:text-white shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Notification Toggle for Settings
export const NotificationToggle = () => {
  const { 
    isSupported, 
    permission, 
    isSubscribed, 
    isLoading,
    requestPermission,
    unsubscribe,
    sendLocalNotification
  } = usePushNotifications();

  const handleToggle = async () => {
    if (isSubscribed) {
      const result = await unsubscribe();
      if (result.success) {
        toast.success('Notifications désactivées');
      }
    } else {
      const result = await requestPermission();
      if (result.success) {
        toast.success('Notifications activées !');
        // Send a test notification
        sendLocalNotification('drbooking', {
          body: 'Les notifications sont maintenant activées !'
        });
      } else if (result.error === 'Permission denied') {
        toast.error('Les notifications sont bloquées. Veuillez les activer dans les paramètres de votre navigateur.');
      }
    }
  };

  const handleTestNotification = () => {
    sendLocalNotification('Test drbooking', {
      body: 'Ceci est une notification de test. Tout fonctionne correctement !'
    });
    toast.success('Notification de test envoyée');
  };

  if (!isSupported) {
    return (
      <div className="flex items-center gap-3 p-4 bg-slate-100 dark:bg-slate-800 rounded-lg">
        <BellOff className="w-5 h-5 text-slate-400" />
        <div>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Notifications non supportées
          </p>
          <p className="text-xs text-slate-500">
            Votre navigateur ne supporte pas les notifications push
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between p-4 bg-slate-100 dark:bg-slate-800 rounded-lg">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <Bell className="w-5 h-5 text-green-500" />
          ) : (
            <BellOff className="w-5 h-5 text-slate-400" />
          )}
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Notifications push
            </p>
            <p className="text-xs text-slate-500">
              {isSubscribed 
                ? 'Vous recevrez des rappels de RDV' 
                : permission === 'denied'
                  ? 'Bloquées dans votre navigateur'
                  : 'Activez pour recevoir des alertes'}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant={isSubscribed ? "outline" : "default"}
          onClick={handleToggle}
          disabled={isLoading || permission === 'denied'}
          data-testid="notification-toggle"
        >
          {isLoading ? 'Chargement...' : isSubscribed ? 'Désactiver' : 'Activer'}
        </Button>
      </div>
      
      {isSubscribed && (
        <Button
          size="sm"
          variant="outline"
          onClick={handleTestNotification}
          className="w-full"
        >
          <Bell className="w-4 h-4 mr-2" />
          Envoyer une notification test
        </Button>
      )}
    </div>
  );
};

export default NotificationBanner;
